import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Plan feature matrix (mirrors backend PLAN_FEATURES)
const PLAN_FEATURES = {
  starter: {
    forecast: true,
    purchasing: false,
    insights: false,
    accuracy: false,
    weather: true,
    inventory: true,
    events: false,
    waste: false,
    pos_integrations: false,
    multi_store: false,
    max_stores: 1,
  },
  pro: {
    forecast: true,
    purchasing: true,
    insights: true,
    accuracy: true,
    weather: true,
    inventory: true,
    events: true,
    waste: true,
    pos_integrations: true,
    multi_store: false,
    max_stores: 1,
  },
  multi: {
    forecast: true,
    purchasing: true,
    insights: true,
    accuracy: true,
    weather: true,
    inventory: true,
    events: true,
    waste: true,
    pos_integrations: true,
    multi_store: true,
    max_stores: 5,
  },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const loadStores = async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
      setActiveStore((prev) => prev || (data[0]?.id ?? null));
    } catch (e) {}
  };

  const loadSubscription = async () => {
    try {
      const { data } = await api.get("/subscription");
      setSubscription(data);
    } catch (e) {
      setSubscription(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("optics_token");
    if (!token) return setUser(false);
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadStores();
        loadSubscription();
      })
      .catch(() => {
        localStorage.removeItem("optics_token");
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("optics_token", data.token);
    setUser(data.user);
    await loadStores();
    await loadSubscription();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("optics_token", data.token);
    setUser(data.user);
    await loadStores();
    await loadSubscription();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("optics_token");
    setUser(false);
    setStores([]);
    setActiveStore(null);
    setSubscription(null);
  };

  const hasFeature = (feature) => {
    const plan = subscription?.current || user?.plan || "starter";
    return PLAN_FEATURES[plan]?.[feature] === true;
  };

  const getMaxStores = () => {
    const plan = subscription?.current || user?.plan || "starter";
    return PLAN_FEATURES[plan]?.max_stores || 1;
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, register, logout, stores, activeStore, setActiveStore, subscription, setSubscription, hasFeature, getMaxStores }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
